import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Typography,
  Divider,
  makeStyles,
  Grid,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";

const useStyles = makeStyles({
  radioChecked: {
    color: "#ec6535 !important",
    "&.Mui-disabled": {
      color: "#ec6535 !important",
      "& svg": {
        color: "#ec6535 !important",
      },
    },
  },
  radioCheckedForecast: {
    color: "#ec6535 !important",
    "&.Mui-disabled": {
      opacity: 0.5,
      "& svg": {
        color: "#ec6535 !important",
      },
    },
  },
  disabled: {},
});

const parseIsIDOCRequired = (val) => {
  if (val === true || val === "Yes") return "Yes";
  if (val === false || val === "No") return "No";
  return "";
};

const CmoLevelConfigSection = forwardRef((props, ref) => {
  const classes = useStyles();
  const {
    initialData = {},
    onChange,
    // onSave,
    // onDiscard,
    editMode = false,
    // onEditModeChange,
    // vendorMosymphonyID,
    // brandOwnerMosymphonyID,
    // disableEdit = false,
    cmoSites = [],
  } = props;

  // Determine if Manufacturing Plant Level options should be disabled
  // Disable only when: exactly 1 site with exactly 1 plant
  // Enable when: multiple sites OR 1 site with multiple plants
  const isPlantLevelDisabled = useMemo(() => {
    // Filter out sites marked for deletion
    const activeSites = cmoSites.filter(
      (site) => !site?.siteDetails?.markForDelete
    );

    // If exactly 1 site, check plant count
    if (activeSites.length === 1) {
      const site = activeSites[0];
      const plants = site?.manufacturingPlantDataList || [];
      const activePlants = plants.filter(
        (plant) => !plant?.mfgPlantDetails?.markForDelete && !plant?.markForDelete
      );

      // Disable only if exactly 1 plant
      return activePlants.length === 1;
    }

    if(activeSites.length === 0){
      return true
    }

    // Multiple sites or no sites → enable (return false)
    return false;
  }, [cmoSites]);

  // Initialize form data from initialData
  const [formData, setFormData] = useState(() => {
    const baseFormData = initialData.mosymphonyDisplayOptions && Array.isArray(initialData.mosymphonyDisplayOptions) ? {
      purchaseOrders: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.purchaseOrder ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.purchaseOrder ? "plantLevel" : "",
      batches: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.batches ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.batches ? "plantLevel" : "",
      inactivePOs: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inactivePOs ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inactivePOs ? "plantLevel" : "",
      inventory: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inventory ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inventory ? "plantLevel" : "",
      forecast: "vendorLevel", // Forecast is always at vendor level (disabled field)
    } : {
      purchaseOrders: "vendorLevel",
      batches: "vendorLevel",
      inactivePOs: "vendorLevel",
      inventory: "vendorLevel",
      forecast: "vendorLevel", // Forecast is always at vendor level
    };
    
    // Forecast is ALWAYS at vendor level (it's a disabled field)
    baseFormData.forecast = "vendorLevel";

    return {
      mosymphonyDisplayOptions: baseFormData,
      isIDOCRequired: parseIsIDOCRequired(initialData.isIDOCRequired),
    };
  });

  // Track if form has changes
  const [hasChanges, setHasChanges] = useState(false);

  // Track previous disabled state to detect changes
  const prevDisabledRef = useRef(isPlantLevelDisabled);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData.mosymphonyDisplayOptions && Array.isArray(initialData.mosymphonyDisplayOptions)) {
      let newFormData = {
        mosymphonyDisplayOptions: {
          purchaseOrders: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.purchaseOrder ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.purchaseOrder ? "plantLevel" : "",
          batches: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.batches ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.batches ? "plantLevel" : "",
          inactivePOs: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inactivePOs ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inactivePOs ? "plantLevel" : "",
          inventory: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inventory ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inventory ? "plantLevel" : "",
          forecast: "vendorLevel", // Forecast is always at vendor level (disabled field)
        },
      };
      
      let wasModified = false;
      // When plant level is disabled, force all options to vendorLevel
      if (isPlantLevelDisabled) {
        const options = newFormData.mosymphonyDisplayOptions;
        const hadPlantLevelOptions = 
          options.purchaseOrders === "plantLevel" ||
          options.batches === "plantLevel" ||
          options.inactivePOs === "plantLevel" ||
          options.inventory === "plantLevel" ||
          !options.forecast;
        
        newFormData.mosymphonyDisplayOptions = {
          purchaseOrders: options.purchaseOrders === "plantLevel" ? "vendorLevel" : options.purchaseOrders || "vendorLevel",
          batches: options.batches === "plantLevel" ? "vendorLevel" : options.batches || "vendorLevel",
          inactivePOs: options.inactivePOs === "plantLevel" ? "vendorLevel" : options.inactivePOs || "vendorLevel",
          inventory: options.inventory === "plantLevel" ? "vendorLevel" : options.inventory || "vendorLevel",
          forecast: "vendorLevel",
        };
        
        wasModified = hadPlantLevelOptions;
      }
      
      const formDataWithIDOC = {
        ...newFormData,
        isIDOCRequired: parseIsIDOCRequired(initialData.isIDOCRequired),
      };

      setFormData(formDataWithIDOC);
      setHasChanges(wasModified);

      // Notify parent if changes were made due to plant level being disabled
      if (wasModified && onChange) {
        onChange(true, formDataWithIDOC);
      }
    }
  }, [initialData, isPlantLevelDisabled, onChange]);

  // Automatically reset to vendor level when plant level becomes disabled
  useEffect(() => {
    // Only trigger when isPlantLevelDisabled changes from false to true
    if (isPlantLevelDisabled && !prevDisabledRef.current) {
      // Check if any option is currently set to plantLevel OR if forecast is not set to vendorLevel
      const currentOptions = formData.mosymphonyDisplayOptions;
      const hasPlantLevelSelections = 
        currentOptions?.purchaseOrders === "plantLevel" ||
        currentOptions?.batches === "plantLevel" ||
        currentOptions?.inactivePOs === "plantLevel" ||
        currentOptions?.inventory === "plantLevel";
      
      const needsForecastReset = currentOptions?.forecast !== "vendorLevel";

      if (hasPlantLevelSelections || needsForecastReset) {
        // Reset all plant-level selections and ensure forecast is at vendor level
        const newFormData = {
          mosymphonyDisplayOptions: {
            purchaseOrders: currentOptions?.purchaseOrders === "plantLevel" ? "vendorLevel" : currentOptions?.purchaseOrders || "vendorLevel",
            batches: currentOptions?.batches === "plantLevel" ? "vendorLevel" : currentOptions?.batches || "vendorLevel",
            inactivePOs: currentOptions?.inactivePOs === "plantLevel" ? "vendorLevel" : currentOptions?.inactivePOs || "vendorLevel",
            inventory: currentOptions?.inventory === "plantLevel" ? "vendorLevel" : currentOptions?.inventory || "vendorLevel",
            forecast: "vendorLevel", // Always set to vendorLevel when plant level is disabled
          },
        };

        setFormData(newFormData);
        setHasChanges(true);

        // Notify parent of changes
        if (onChange) {
          onChange(true, newFormData);
        }
      }
    }

    // Update the previous disabled state
    prevDisabledRef.current = isPlantLevelDisabled;
  }, [isPlantLevelDisabled, formData.mosymphonyDisplayOptions, onChange]);

  // Ensure forecast is always set to vendorLevel when plant level is disabled (even on initial load)
  useEffect(() => {
    if (isPlantLevelDisabled && formData.mosymphonyDisplayOptions?.forecast !== "vendorLevel") {
      const newFormData = {
        mosymphonyDisplayOptions: {
          ...formData.mosymphonyDisplayOptions,
          forecast: "vendorLevel",
        },
      };
      
      setFormData(newFormData);
      
      // Only mark as changed if we're modifying from an existing non-empty value
      if (formData.mosymphonyDisplayOptions?.forecast && formData.mosymphonyDisplayOptions?.forecast !== "") {
        setHasChanges(true);
        if (onChange) {
          onChange(true, newFormData);
        }
      }
    }
  }, [isPlantLevelDisabled, formData.mosymphonyDisplayOptions?.forecast, onChange]);

  // Handle radio button changes
  const handleMosymphonyDisplayChange = (category, level) => {
    const newFormData = {
      ...formData,
      mosymphonyDisplayOptions: {
        ...formData.mosymphonyDisplayOptions,
        [category]: level,
      },
    };

    setFormData(newFormData);
    setHasChanges(true);

    // Notify parent of changes
    if (onChange) {
      onChange(true, newFormData);
    }
  };

  const handleIsIDOCRequiredChange = (event) => {
    const newFormData = { ...formData, isIDOCRequired: event.target.value };
    setFormData(newFormData);
    setHasChanges(true);
    if (onChange) onChange(true, newFormData);
  };

  // Reset form to initial data
  const resetForm = () => {
    let resetFormData;
    if (initialData.mosymphonyDisplayOptions && Array.isArray(initialData.mosymphonyDisplayOptions)) {
      resetFormData = {
        mosymphonyDisplayOptions: {
          purchaseOrders: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.purchaseOrder ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.purchaseOrder ? "plantLevel" : "",
          batches: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.batches ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.batches ? "plantLevel" : "",
          inactivePOs: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inactivePOs ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inactivePOs ? "plantLevel" : "",
          inventory: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.inventory ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.inventory ? "plantLevel" : "",
          forecast: initialData.mosymphonyDisplayOptions[0]?.objectsToUseVendorLevelData?.forecast ? "vendorLevel" : initialData.mosymphonyDisplayOptions[0]?.objectsToUseMfgPlantLevelData?.forecast ? "plantLevel" : "",
        },
      };
    } else {
      resetFormData = {
        mosymphonyDisplayOptions: {
          purchaseOrders: "vendorLevel",
          batches: "vendorLevel",
          inactivePOs: "vendorLevel",
          inventory: "vendorLevel",
          forecast: "vendorLevel", // Forecast is always at vendor level
        },
      };
    }
    
    // When plant level is disabled (1 site with 1 plant), force all options to vendorLevel
    if (isPlantLevelDisabled) {
      const options = resetFormData.mosymphonyDisplayOptions;
      resetFormData.mosymphonyDisplayOptions = {
        purchaseOrders: options.purchaseOrders === "plantLevel" ? "vendorLevel" : options.purchaseOrders || "vendorLevel",
        batches: options.batches === "plantLevel" ? "vendorLevel" : options.batches || "vendorLevel",
        inactivePOs: options.inactivePOs === "plantLevel" ? "vendorLevel" : options.inactivePOs || "vendorLevel",
        inventory: options.inventory === "plantLevel" ? "vendorLevel" : options.inventory || "vendorLevel",
        forecast: "vendorLevel", // Always vendorLevel when plant level is disabled
      };
    }
    
    setFormData({
      ...resetFormData,
      isIDOCRequired: parseIsIDOCRequired(initialData.isIDOCRequired),
    });
    setHasChanges(false);

    if (onChange) {
      onChange(false);
    }
  };

  // Force reset to vendor level (used when site count becomes 1)
  const resetToVendorLevel = () => {
    setFormData({
      mosymphonyDisplayOptions: {
        purchaseOrders: "vendorLevel",
        batches: "vendorLevel",
        inactivePOs: "vendorLevel",
        inventory: "vendorLevel",
        forecast: "vendorLevel", // Forecast should also be at vendor level
      },
    });
    setHasChanges(true); // Mark as changed so user can save

    if (onChange) {
      onChange(true);
    }
  };

  // Get payload for saving
  const getPayload = () => {
    const options = formData.mosymphonyDisplayOptions;

    // Transform to boolean objects structure
    const objectsToUseVendorLevelData = {
      purchaseOrder: options.purchaseOrders === "vendorLevel",
      batches: options.batches === "vendorLevel",
      inactivePOs: options.inactivePOs === "vendorLevel",
      inventory: options.inventory === "vendorLevel",
      forecast: true, // Forecast is always at vendor level
    };

    const objectsToUseMfgPlantLevelData = {
      purchaseOrder: options.purchaseOrders === "plantLevel",
      batches: options.batches === "plantLevel",
      inactivePOs: options.inactivePOs === "plantLevel",
      inventory: options.inventory === "plantLevel",
      forecast: false, // Forecast is never at plant level
    };

    return {
      vendorPlantDetails: {
        mosymphonyDisplayOptions: [{
          objectsToUseVendorLevelData,
          objectsToUseMfgPlantLevelData,
        }],
        isIDOCRequired: formData.isIDOCRequired === "Yes",
      },
      updateCMOLevelConfigs: true,
    };
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    resetForm,
    resetToVendorLevel,
    getPayload,
    getFormData: () => formData,
    hasChanges: () => hasChanges,
  }));

  return (
    <div className="cmo-stepper-wrapper cmo-level-config-wrapper">
      <div className="stepper-container">
        <TableContainer component={Paper} style={{ maxWidth: 1200 }}>
          <Table>
            <TableHead style={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell style={{ minWidth: 220, fontWeight: 600 }}>
                  Mosymphony Display Options
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  Purchase Orders Data At
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  Batches Data At
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  Inactive POs Data At
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  Inventory Data At
                </TableCell>
                <TableCell align="center" style={{ fontWeight: 600 }}>
                  Forecast Data At
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Vendor Level (Vendor ID)</TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="purchaseOrders"
                    checked={
                      formData.mosymphonyDisplayOptions?.purchaseOrders ===
                      "vendorLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("purchaseOrders", "vendorLevel")
                    }
                    disabled={!editMode}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="batches"
                    checked={
                      formData.mosymphonyDisplayOptions?.batches === "vendorLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("batches", "vendorLevel")
                    }
                    disabled={!editMode}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="inactivePOs"
                    checked={
                      formData.mosymphonyDisplayOptions?.inactivePOs ===
                      "vendorLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("inactivePOs", "vendorLevel")
                    }
                    disabled={!editMode}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="inventory"
                    checked={
                      formData.mosymphonyDisplayOptions?.inventory === "vendorLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("inventory", "vendorLevel")
                    }
                    disabled={!editMode}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="forecast"
                    checked={
                      formData.mosymphonyDisplayOptions?.forecast === "vendorLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("forecast", "vendorLevel")
                    }
                    disabled={true}
                    classes={{
                      checked: classes.radioCheckedForecast,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Manufacturing Plant Level (Manufacturing Plant ID)
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="purchaseOrders"
                    checked={
                      formData.mosymphonyDisplayOptions?.purchaseOrders ===
                      "plantLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("purchaseOrders", "plantLevel")
                    }
                    disabled={!editMode || isPlantLevelDisabled}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="batches"
                    checked={
                      formData.mosymphonyDisplayOptions?.batches === "plantLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("batches", "plantLevel")
                    }
                    disabled={!editMode || isPlantLevelDisabled}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="inactivePOs"
                    checked={
                      formData.mosymphonyDisplayOptions?.inactivePOs === "plantLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("inactivePOs", "plantLevel")
                    }
                    disabled={!editMode || isPlantLevelDisabled}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="inventory"
                    checked={
                      formData.mosymphonyDisplayOptions?.inventory === "plantLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("inventory", "plantLevel")
                    }
                    disabled={!editMode || isPlantLevelDisabled}
                    classes={{
                      checked: classes.radioChecked,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Radio
                    color="primary"
                    name="forecast"
                    checked={
                      formData.mosymphonyDisplayOptions?.forecast === "plantLevel"
                    }
                    onChange={() =>
                      handleMosymphonyDisplayChange("forecast", "plantLevel")
                    }
                    disabled={true}
                    classes={{
                      checked: classes.radioCheckedForecast,
                      disabled: classes.disabled,
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1 }} />

        <Grid container spacing={3} style={{ marginTop: 24, maxWidth: 600 }}>
          <Grid item xs={12} sm={6}>
            <FormControl style={{ width: "100%" }}>
              <Typography component="label" htmlFor="isIDOCRequired" style={{ fontWeight: 700, margin: "0 0 8px 0", lineHeight: "24px", fontSize: 16 }}>
                ERP-to-ERP Connectivity{editMode && <span style={{ color: "red" }}> *</span>}
              </Typography>
              {!editMode ? (
                <Typography>{formData.isIDOCRequired || ""}</Typography>
              ) : (
                <Select
                  id="isIDOCRequired"
                  name="isIDOCRequired"
                  value={formData.isIDOCRequired || ""}
                  onChange={handleIsIDOCRequiredChange}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select from the list</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </div>
  );
});

export default CmoLevelConfigSection;
